/**
 * EditableStructuredPreview — document-style editable meeting minutes rendered
 * across one or more letter-sized <Paper> pages. Sections flow onto additional
 * pages when content exceeds 8.49in of usable column height (matching the
 * docx template's 1in top + 1.51in bottom margins).
 *
 * Editable text spans use EditableText for contentEditable-with-React-safety.
 * When readOnly, the same layout renders without edit affordances.
 */
import { useCallback, useMemo, useState, type DragEvent, type ReactNode } from 'react';
import type {
  StructuredContent,
  Topic,
  Bullet,
  ActionItem,
  AttendeeGroup,
} from '@ojb/shared';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { EditableText } from './EditableText.tsx';
import { PagedDocument, type DocumentSection } from './PagedDocument.tsx';
import { OjbMemberPicker } from './OjbMemberPicker.tsx';

interface Props {
  content: StructuredContent;
  onChange: (next: StructuredContent) => void;
  readOnly?: boolean;
  /** When false, the bottom-right page number on each sheet is
   *  hidden. Default true. Threaded down to PagedDocument → Paper. */
  showPageNumbers?: boolean;
}

export function EditableStructuredPreview({
  content,
  onChange,
  readOnly = false,
  showPageNumbers = true,
}: Props) {
  // Topic drag-reorder state — lifted here so all TopicBlockView
  // instances across paged sections share the same drag source/target
  // without prop drilling through PagedDocument. Only active when
  // readOnly is false.
  const [draggingTopicIndex, setDraggingTopicIndex] = useState<number | null>(
    null,
  );
  const [dragOverTopicIndex, setDragOverTopicIndex] = useState<number | null>(
    null,
  );
  const handleTopicDragStart = useCallback(
    (ti: number, e: DragEvent<HTMLDivElement>) => {
      setDraggingTopicIndex(ti);
      // Firefox requires dataTransfer.setData() for drag to initiate.
      // The string is cosmetic — we track indices via React state.
      e.dataTransfer.setData('text/plain', String(ti));
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );
  const handleTopicDragOver = useCallback(
    (ti: number, e: DragEvent<HTMLDivElement>) => {
      if (draggingTopicIndex === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverTopicIndex !== ti) setDragOverTopicIndex(ti);
    },
    [draggingTopicIndex, dragOverTopicIndex],
  );
  const handleTopicDragEnd = useCallback(() => {
    setDraggingTopicIndex(null);
    setDragOverTopicIndex(null);
  }, []);

  const update = useMemo(
    () => ({
      setTitle: (v: string) => onChange({ ...content, title: v }),
      setLocation: (v: string) => onChange({ ...content, location: v }),
      setProject: (v: string) => onChange({ ...content, project: v }),
      setMeetingDate: (v: string) => onChange({ ...content, meetingDate: v }),
      setNotesDate: (v: string) => onChange({ ...content, notesDate: v }),
      setMeetingFormat: (v: StructuredContent['meetingFormat']) =>
        onChange({ ...content, meetingFormat: v }),

      setTopicTitle: (ti: number, v: string) => {
        const topics = [...content.topics];
        topics[ti] = { ...topics[ti], title: v };
        onChange({ ...content, topics });
      },
      addTopic: () => {
        const topics = [
          ...content.topics,
          { title: 'New topic', kind: 'discussion' as const, bullets: [] },
        ];
        onChange({ ...content, topics });
      },
      removeTopic: (ti: number) => {
        const topics = content.topics.filter((_, i) => i !== ti);
        onChange({ ...content, topics });
      },
      /**
       * Drag-to-reorder helper. `to` is the index the dragged topic
       * should land at AFTER removal of itself. Noop when from===to
       * or either index is out of range.
       */
      moveTopic: (from: number, to: number) => {
        if (from === to) return;
        if (from < 0 || from >= content.topics.length) return;
        if (to < 0 || to >= content.topics.length) return;
        const topics = [...content.topics];
        const [moved] = topics.splice(from, 1);
        topics.splice(to, 0, moved);
        onChange({ ...content, topics });
      },

      setBullet: (ti: number, bi: number, patch: Partial<Bullet>) => {
        const topics = [...content.topics];
        const bullets = [...topics[ti].bullets];
        bullets[bi] = { ...bullets[bi], ...patch };
        topics[ti] = { ...topics[ti], bullets };
        onChange({ ...content, topics });
      },
      addBulletAfter: (ti: number, bi: number) => {
        const topics = [...content.topics];
        const bullets = [...topics[ti].bullets];
        bullets.splice(bi + 1, 0, { body: '', isDirectQuote: false });
        topics[ti] = { ...topics[ti], bullets };
        onChange({ ...content, topics });
      },
      removeBullet: (ti: number, bi: number) => {
        const topics = [...content.topics];
        const bullets = topics[ti].bullets.filter((_, i) => i !== bi);
        topics[ti] = { ...topics[ti], bullets };
        onChange({ ...content, topics });
      },

      setAction: (i: number, patch: Partial<ActionItem>) => {
        const actionItems = [...content.actionItems];
        actionItems[i] = { ...actionItems[i], ...patch };
        onChange({ ...content, actionItems });
      },
      addAction: () => {
        const actionItems = [
          ...content.actionItems,
          { description: '', status: 'open' as const },
        ];
        onChange({ ...content, actionItems });
      },
      removeAction: (i: number) => {
        const actionItems = content.actionItems.filter((_, idx) => idx !== i);
        onChange({ ...content, actionItems });
      },

      setAttendeeGroup: (i: number, patch: Partial<AttendeeGroup>) => {
        const groups = [...content.attendeeGroups];
        groups[i] = { ...groups[i], ...patch };
        onChange({ ...content, attendeeGroups: groups });
      },
      setAttendeeMembers: (i: number, membersStr: string) => {
        // Members separated by semicolons. Previously comma, but names
        // often carry trailing credentials ("Sarah Chen, FASLA") and
        // comma-splitting was eating the credentials as separate
        // attendees. Semicolon keeps names whole.
        const groups = [...content.attendeeGroups];
        const members = membersStr.split(';').map((m) => m.trim()).filter(Boolean);
        groups[i] = { ...groups[i], members };
        onChange({ ...content, attendeeGroups: groups });
      },
      setAttendeeMembersArray: (i: number, members: string[]) => {
        const groups = [...content.attendeeGroups];
        groups[i] = { ...groups[i], members };
        onChange({ ...content, attendeeGroups: groups });
      },
      moveAttendeeGroup: (from: number, to: number) => {
        if (from === to) return;
        if (from < 0 || from >= content.attendeeGroups.length) return;
        if (to < 0 || to >= content.attendeeGroups.length) return;
        const groups = [...content.attendeeGroups];
        const [moved] = groups.splice(from, 1);
        groups.splice(to, 0, moved);
        onChange({ ...content, attendeeGroups: groups });
      },
      /**
       * Move a member from one firm group to another. No-op when src
       * and dest are the same group. Preserves relative order inside
       * the target group (appended at the end).
       */
      moveAttendeeMember: (
        fromGroupIdx: number,
        fromMemberIdx: number,
        toGroupIdx: number,
      ) => {
        if (fromGroupIdx === toGroupIdx) return;
        const groups = content.attendeeGroups.map((g) => ({
          ...g,
          members: [...g.members],
        }));
        const src = groups[fromGroupIdx];
        const dest = groups[toGroupIdx];
        if (!src || !dest) return;
        const [moved] = src.members.splice(fromMemberIdx, 1);
        if (moved === undefined) return;
        if (!dest.members.includes(moved)) dest.members.push(moved);
        onChange({ ...content, attendeeGroups: groups });
      },
      addAttendeeGroup: (isOjb: boolean) => {
        onChange({
          ...content,
          attendeeGroups: [
            ...content.attendeeGroups,
            { firm: isOjb ? 'OJB' : 'NEW FIRM', isOjb, members: [] },
          ],
        });
      },
      removeAttendeeGroup: (i: number) => {
        const groups = content.attendeeGroups.filter((_, idx) => idx !== i);
        onChange({ ...content, attendeeGroups: groups });
      },
    }),
    [content, onChange],
  );

  const handleTopicDrop = useCallback(
    (ti: number, e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (draggingTopicIndex !== null && draggingTopicIndex !== ti) {
        update.moveTopic(draggingTopicIndex, ti);
      }
      setDraggingTopicIndex(null);
      setDragOverTopicIndex(null);
    },
    [draggingTopicIndex, update],
  );

  // Build sections for the paginator. Each section is a top-level block that
  // stays together on a single page (no mid-section breaks).
  const sections = useMemo<DocumentSection[]>(() => {
    const result: DocumentSection[] = [];

    // Header (document-type label + title + metadata)
    result.push({
      key: 'header',
      renderMeasure: () => <HeaderBlock content={content} readOnly />,
      renderDisplay: () => (
        <HeaderBlock
          content={content}
          readOnly={readOnly}
          onTitleChange={update.setTitle}
          onProjectChange={update.setProject}
          onLocationChange={update.setLocation}
          onFormatChange={update.setMeetingFormat}
          onMeetingDateChange={update.setMeetingDate}
          onNotesDateChange={update.setNotesDate}
        />
      ),
    });

    // Attendees
    result.push({
      key: 'attendees',
      renderMeasure: () => <AttendeesBlock content={content} readOnly />,
      renderDisplay: () => (
        <AttendeesBlock
          content={content}
          readOnly={readOnly}
          onGroupChange={update.setAttendeeGroup}
          onMembersChange={update.setAttendeeMembers}
          onAddGroup={update.addAttendeeGroup}
          onRemoveGroup={update.removeAttendeeGroup}
        />
      ),
    });

    // Action items
    result.push({
      key: 'action-items',
      renderMeasure: () => <ActionItemsBlock content={content} readOnly />,
      renderDisplay: () => (
        <ActionItemsBlock
          content={content}
          readOnly={readOnly}
          onChange={update.setAction}
          onAdd={update.addAction}
          onRemove={update.removeAction}
        />
      ),
    });

    // Meeting-notes header + first topic as one section (so "Meeting Notes"
    // heading never ends up as an orphan at the bottom of a page)
    if (content.topics.length === 0) {
      result.push({
        key: 'notes-empty',
        renderMeasure: () => <NotesHeader readOnly />,
        renderDisplay: () => (
          <NotesHeader readOnly={readOnly} onAddTopic={update.addTopic} empty />
        ),
      });
    } else {
      result.push({
        key: 'notes-first',
        renderMeasure: () => (
          <>
            <NotesHeader readOnly />
            <div style={{ marginTop: '1rem' }}>
              <TopicBlockView
                topic={content.topics[0]}
                readOnly
                onTitleChange={() => undefined}
                onBulletChange={() => undefined}
                onBulletAdd={() => undefined}
                onBulletRemove={() => undefined}
                onRemove={() => undefined}
              />
            </div>
          </>
        ),
        renderDisplay: () => (
          <>
            <NotesHeader readOnly={readOnly} onAddTopic={update.addTopic} />
            <div style={{ marginTop: '1rem' }}>
              <TopicBlockView
                topic={content.topics[0]}
                readOnly={readOnly}
                onTitleChange={(v) => update.setTopicTitle(0, v)}
                onBulletChange={(bi, patch) => update.setBullet(0, bi, patch)}
                onBulletAdd={(bi) => update.addBulletAfter(0, bi)}
                onBulletRemove={(bi) => update.removeBullet(0, bi)}
                onRemove={() => update.removeTopic(0)}
                topicIndex={0}
                isDragging={draggingTopicIndex === 0}
                isDragOver={dragOverTopicIndex === 0}
                onDragStart={handleTopicDragStart}
                onDragOver={handleTopicDragOver}
                onDragEnd={handleTopicDragEnd}
                onDrop={handleTopicDrop}
              />
            </div>
          </>
        ),
      });

      // Remaining topics — one section each so they paginate independently
      content.topics.slice(1).forEach((topic, i) => {
        const ti = i + 1;
        result.push({
          key: `topic-${ti}`,
          renderMeasure: () => (
            <TopicBlockView
              topic={topic}
              readOnly
              onTitleChange={() => undefined}
              onBulletChange={() => undefined}
              onBulletAdd={() => undefined}
              onBulletRemove={() => undefined}
              onRemove={() => undefined}
            />
          ),
          renderDisplay: () => (
            <TopicBlockView
              topic={topic}
              readOnly={readOnly}
              onTitleChange={(v) => update.setTopicTitle(ti, v)}
              onBulletChange={(bi, patch) => update.setBullet(ti, bi, patch)}
              onBulletAdd={(bi) => update.addBulletAfter(ti, bi)}
              onBulletRemove={(bi) => update.removeBullet(ti, bi)}
              onRemove={() => update.removeTopic(ti)}
              topicIndex={ti}
              isDragging={draggingTopicIndex === ti}
              isDragOver={dragOverTopicIndex === ti}
              onDragStart={handleTopicDragStart}
              onDragOver={handleTopicDragOver}
              onDragEnd={handleTopicDragEnd}
              onDrop={handleTopicDrop}
            />
          ),
        });
      });
    }

    return result;
  }, [
    content,
    readOnly,
    update,
    draggingTopicIndex,
    dragOverTopicIndex,
    handleTopicDragStart,
    handleTopicDragOver,
    handleTopicDragEnd,
    handleTopicDrop,
  ]);

  return <PagedDocument sections={sections} showPageNumbers={showPageNumbers} />;
}

// ─── Section components ────────────────────────────────────────────────────

interface HeaderBlockProps {
  content: StructuredContent;
  readOnly: boolean;
  onTitleChange?: (v: string) => void;
  onProjectChange?: (v: string) => void;
  onLocationChange?: (v: string) => void;
  onFormatChange?: (v: StructuredContent['meetingFormat']) => void;
  onMeetingDateChange?: (v: string) => void;
  onNotesDateChange?: (v: string) => void;
}

const FORMAT_LABELS: Record<string, string> = {
  in_person: 'In person',
  video: 'Video',
  phone: 'Phone',
  hybrid: 'Hybrid',
};

function HeaderBlock({
  content,
  readOnly,
  onTitleChange,
  onProjectChange,
  onLocationChange,
  onFormatChange,
  onMeetingDateChange,
  onNotesDateChange,
}: HeaderBlockProps) {
  return (
    <header>
      {/* Variant kicker intentionally NOT rendered — pilot uses a
          single unified format. Title is now flush with the top of
          the header block. */}
      <h1 className="text-2xl font-semibold leading-tight tracking-tight text-charcoal">
        {readOnly || !onTitleChange ? (
          content.title || <span className="italic text-smoke">Untitled</span>
        ) : (
          <EditableText
            value={content.title}
            onChange={onTitleChange}
            placeholder="Meeting title"
            ariaLabel="Meeting title"
          />
        )}
      </h1>
      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px] leading-relaxed">
        <MetaDate
          label="Date"
          value={content.meetingDate}
          onChange={onMeetingDateChange}
          readOnly={readOnly}
        />
        <MetaDate
          label="Notes"
          value={content.notesDate}
          onChange={onNotesDateChange}
          readOnly={readOnly}
        />
        <MetaEditable
          label="Project"
          value={content.project}
          onChange={onProjectChange}
          readOnly={readOnly}
        />
        <MetaEditable
          label="Location"
          value={content.location ?? ''}
          onChange={onLocationChange}
          readOnly={readOnly}
          placeholder="-"
        />
        <MetaFormat
          value={content.meetingFormat}
          onChange={onFormatChange}
          readOnly={readOnly}
        />
      </dl>
    </header>
  );
}

function MetaFormat({
  value,
  onChange,
  readOnly,
}: {
  value: StructuredContent['meetingFormat'];
  onChange?: (v: StructuredContent['meetingFormat']) => void;
  readOnly: boolean;
}) {
  return (
    <>
      <dt className="pt-0.5 text-[11px] font-bold uppercase tracking-wider text-charcoal/60">
        Format
      </dt>
      <dd className="text-charcoal">
        {readOnly || !onChange ? (
          FORMAT_LABELS[value] ?? '-'
        ) : (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value as StructuredContent['meetingFormat'])}
            className="-mx-0.5 rounded-sm border-0 bg-transparent px-0.5 py-0 text-[13px] text-charcoal hover:bg-pine/5 focus:bg-pine/5 focus:outline-none"
          >
            {Object.entries(FORMAT_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        )}
      </dd>
    </>
  );
}

interface AttendeesBlockProps {
  content: StructuredContent;
  readOnly: boolean;
  onGroupChange?: (i: number, patch: Partial<AttendeeGroup>) => void;
  onMembersChange?: (i: number, membersStr: string) => void;
  onAddGroup?: (isOjb: boolean) => void;
  onRemoveGroup?: (i: number) => void;
}

function AttendeesBlock({
  content,
  readOnly,
  onGroupChange,
  onMembersChange,
  onAddGroup,
  onRemoveGroup,
}: AttendeesBlockProps) {
  return (
    <Section
      title="Attendees"
      addSlot={
        !readOnly &&
        onAddGroup && (
          <SectionAddMenu
            items={[
              { label: 'External firm', onClick: () => onAddGroup(false) },
              { label: 'OJB group', onClick: () => onAddGroup(true) },
            ]}
          />
        )
      }
    >
      {content.attendeeGroups.length === 0 ? (
        <EmptyHint
          text="No attendees recorded."
          action={
            !readOnly &&
            onAddGroup && (
              <>
                <button
                  type="button"
                  onClick={() => onAddGroup(true)}
                  className="text-pine hover:underline"
                >
                  Add OJB
                </button>
                {' or '}
                <button
                  type="button"
                  onClick={() => onAddGroup(false)}
                  className="text-pine hover:underline"
                >
                  Add external firm
                </button>
              </>
            )
          }
        />
      ) : (
        <ul className="space-y-3 text-[13px] leading-relaxed">
          {content.attendeeGroups.map((group, i) => (
            <li key={i} className="group">
              <div className="flex items-start gap-3">
                <span className="inline-block min-w-[6ch] font-bold uppercase tracking-wider">
                  {readOnly || !onGroupChange ? (
                    group.firm
                  ) : (
                    <EditableText
                      value={group.firm}
                      onChange={(v) => onGroupChange(i, { firm: v })}
                      placeholder="FIRM"
                      className="uppercase"
                    />
                  )}
                </span>
                <span className="flex-1">
                  {readOnly ? (
                    group.members.join('; ')
                  ) : group.isOjb ? (
                    // OJB group: render as pills + typeahead picker
                    <OjbAttendeeEditor
                      members={group.members}
                      onMembersChange={(newMembers) =>
                        onMembersChange?.(i, newMembers.join('; '))
                      }
                    />
                  ) : (
                    // External firm: semicolon-separated editable text
                    // (comma would split names with credentials like
                    // "Sarah Chen, FASLA" into two attendees).
                    <EditableText
                      value={group.members.join('; ')}
                      onChange={(v) => onMembersChange?.(i, v)}
                      placeholder="Name; Name, FASLA"
                    />
                  )}
                </span>
                {!readOnly && onRemoveGroup && (
                  <RemoveButton onClick={() => onRemoveGroup(i)} label="Remove firm" />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

interface ActionItemsBlockProps {
  content: StructuredContent;
  readOnly: boolean;
  onChange?: (i: number, patch: Partial<ActionItem>) => void;
  onAdd?: () => void;
  onRemove?: (i: number) => void;
}

function ActionItemsBlock({
  content,
  readOnly,
  onChange,
  onAdd,
  onRemove,
}: ActionItemsBlockProps) {
  return (
    <Section
      title="Action Items"
      addSlot={
        !readOnly &&
        onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="text-[11px] text-smoke opacity-0 transition-opacity hover:text-pine group-hover/section:opacity-100"
          >
            + add
          </button>
        )
      }
    >
      {content.actionItems.length === 0 ? (
        <EmptyHint
          text="No action items."
          action={
            !readOnly &&
            onAdd && (
              <button type="button" onClick={onAdd} className="text-pine hover:underline">
                Add one
              </button>
            )
          }
        />
      ) : (
        <ul className="space-y-1 text-[13px] leading-relaxed">
          {content.actionItems.map((ai, i) => (
            <li key={i} className="group flex items-start gap-3">
              <span aria-hidden className="mt-[0.1em] shrink-0 font-bold text-pine">
                +
              </span>
              <span className="flex-1">
                {readOnly || !onChange ? (
                  ai.description
                ) : (
                  <EditableText
                    value={ai.description}
                    onChange={(v) => onChange(i, { description: v })}
                    placeholder="What needs to happen"
                  />
                )}
                {(ai.assigneeName || (!readOnly && onChange)) && (
                  <span className="text-smoke">
                    {' - '}
                    {readOnly || !onChange ? (
                      ai.assigneeName
                    ) : (
                      <EditableText
                        value={ai.assigneeName ?? ''}
                        onChange={(v) => onChange(i, { assigneeName: v || undefined })}
                        placeholder="Assignee"
                      />
                    )}
                  </span>
                )}
                {(ai.dueDate || (!readOnly && onChange)) && (
                  <span className="text-smoke">
                    {' ('}
                    {readOnly || !onChange ? (
                      'due ' + ai.dueDate
                    ) : (
                      <>
                        {'due '}
                        <EditableText
                          value={ai.dueDate ?? ''}
                          onChange={(v) => onChange(i, { dueDate: v || undefined })}
                          placeholder="YYYY-MM-DD"
                        />
                      </>
                    )}
                    {')'}
                  </span>
                )}
              </span>
              {!readOnly && onRemove && (
                <RemoveButton onClick={() => onRemove(i)} label="Remove action" />
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function NotesHeader({
  readOnly,
  onAddTopic,
  empty,
}: {
  readOnly: boolean;
  onAddTopic?: () => void;
  empty?: boolean;
}) {
  return (
    <Section
      title="Meeting Notes"
      addSlot={
        !readOnly &&
        onAddTopic && (
          <button
            type="button"
            onClick={onAddTopic}
            className="text-[11px] text-smoke opacity-0 transition-opacity hover:text-pine group-hover/section:opacity-100"
          >
            + topic
          </button>
        )
      }
    >
      {empty && (
        <EmptyHint
          text="No topics yet."
          action={
            !readOnly &&
            onAddTopic && (
              <button type="button" onClick={onAddTopic} className="text-pine hover:underline">
                Add a topic
              </button>
            )
          }
        />
      )}
    </Section>
  );
}

interface TopicBlockViewProps {
  topic: Topic;
  readOnly: boolean;
  onTitleChange: (v: string) => void;
  onBulletChange: (bi: number, patch: Partial<Bullet>) => void;
  onBulletAdd: (bi: number) => void;
  onBulletRemove: (bi: number) => void;
  onRemove: () => void;
  /** Drag-to-reorder wiring. All optional; the block renders
   *  non-draggable when these aren't provided (readOnly / measure
   *  passes). */
  topicIndex?: number;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (ti: number, e: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (ti: number, e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  onDrop?: (ti: number, e: DragEvent<HTMLDivElement>) => void;
}

function TopicBlockView({
  topic,
  readOnly,
  onTitleChange,
  onBulletChange,
  onBulletAdd,
  onBulletRemove,
  onRemove,
  topicIndex,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: TopicBlockViewProps) {
  const canDrag =
    !readOnly &&
    topicIndex !== undefined &&
    !!onDragStart &&
    !!onDragOver &&
    !!onDragEnd &&
    !!onDrop;

  return (
    <div
      className={
        'group/topic rounded-md transition-all ' +
        (isDragging ? 'opacity-40 ' : '') +
        (isDragOver
          ? 'ring-2 ring-pine/50 ring-offset-2 ring-offset-white '
          : '')
      }
      draggable={canDrag}
      onDragStart={
        canDrag && topicIndex !== undefined
          ? (e) => onDragStart!(topicIndex, e)
          : undefined
      }
      onDragOver={
        canDrag && topicIndex !== undefined
          ? (e) => onDragOver!(topicIndex, e)
          : undefined
      }
      onDragEnd={canDrag ? () => onDragEnd!() : undefined}
      onDrop={
        canDrag && topicIndex !== undefined
          ? (e) => onDrop!(topicIndex, e)
          : undefined
      }
    >
      <div className="flex items-baseline gap-2">
        {canDrag && (
          <span
            aria-hidden
            title="Drag to reorder topics"
            className="-ml-1 shrink-0 cursor-grab select-none text-[13px] leading-none text-charcoal/25 opacity-0 transition-opacity hover:text-pine group-hover/topic:opacity-100 active:cursor-grabbing"
          >
            ⠿
          </span>
        )}
        <h3 className="text-[13px] font-bold text-charcoal">
          {readOnly ? (
            topic.title + ':'
          ) : (
            <>
              <EditableText
                value={topic.title}
                onChange={onTitleChange}
                placeholder="Topic title"
              />
              :
            </>
          )}
        </h3>
        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[11px] text-smoke/70 opacity-0 transition-opacity hover:text-red-600 group-hover/topic:opacity-100"
            aria-label="Remove topic"
            title="Remove topic"
          >
            ✕
          </button>
        )}
      </div>
      <ul className="mt-1.5 space-y-1 text-[13px] leading-relaxed">
        {topic.bullets.length === 0 && !readOnly && (
          <li className="text-smoke">
            <button
              type="button"
              onClick={() => onBulletAdd(-1)}
              className="italic text-smoke hover:text-pine hover:underline"
            >
              Add a bullet…
            </button>
          </li>
        )}
        {topic.bullets.map((b, bi) => (
          <li key={bi} className="group/bullet flex gap-3">
            <span aria-hidden className="mt-[0.15em] shrink-0 text-smoke">
              ●
            </span>
            <span className="flex-1">
              {/* Speaker prefix intentionally NOT rendered. Per OJB
                  pilot convention the speaker's name is baked into
                  the body prose in third person ("Patrick Martin
                  raised RFI 042…"), not surfaced as a separate
                  "Speaker:" label. The `speaker` field on the bullet
                  is preserved on the data so older docs round-trip
                  cleanly, but it's invisible. */}
              {readOnly ? (
                <span className={b.isDirectQuote ? 'italic' : ''}>{b.body}</span>
              ) : (
                <EditableText
                  value={b.body}
                  onChange={(v) => onBulletChange(bi, { body: v })}
                  placeholder="Discussion point"
                  onEnter={() => onBulletAdd(bi)}
                  onDeleteEmpty={() => onBulletRemove(bi)}
                  className={b.isDirectQuote ? 'italic' : ''}
                />
              )}
              {b.ambiguityFlag && (
                <span
                  title={b.ambiguityFlag}
                  // Brand-aligned terracotta tint — much softer than
                  // the previous amber. terra-cotta/10 bg + /25 border
                  // reads as a quiet annotation rather than a warning
                  // banner. Title attribute still carries the full
                  // flag text on hover.
                  className="ml-1 inline-flex cursor-help items-center gap-1 rounded-full border border-terra-cotta/25 bg-terra-cotta/10 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-[0.12em] text-terra-cotta"
                >
                  <span aria-hidden>⚠</span>
                  Ambiguous
                </span>
              )}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => onBulletRemove(bi)}
                className="shrink-0 self-start text-[11px] text-smoke/70 opacity-0 transition-opacity hover:text-red-600 group-hover/bullet:opacity-100"
                aria-label="Remove bullet"
                title="Remove bullet"
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Renders OJB staff members as removable pills + a typeahead picker below.
 * External-firm members stay simple comma-separated text; OJB uses this
 * because the /ojb-team endpoint gives us a curated roster to pick from.
 */
function OjbAttendeeEditor({
  members,
  onMembersChange,
}: {
  members: string[];
  onMembersChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-1.5">
      {members.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {members.map((name, i) => (
            <span
              key={`${name}-${i}`}
              className="group/pill inline-flex items-center gap-1 rounded-sm bg-pine/10 px-2 py-0.5 text-[12px] text-pine"
            >
              {name}
              <button
                type="button"
                onClick={() => onMembersChange(members.filter((_, idx) => idx !== i))}
                className="text-pine/60 opacity-0 transition-opacity hover:text-red-600 group-hover/pill:opacity-100"
                aria-label={`Remove ${name}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <OjbMemberPicker
        onSelect={(name) => {
          if (!members.includes(name)) {
            onMembersChange([...members, name]);
          }
        }}
        excludeNames={members}
      />
    </div>
  );
}

// ─── Shared subcomponents ─────────────────────────────────────────────────

// (Read-only `Meta` helper removed during audit-fix sweep — it was
// superseded by `MetaEditable` everywhere in the preview. Recoverable
// from git history if a non-editable variant is needed again.)

function MetaEditable({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  readOnly: boolean;
  placeholder?: string;
}) {
  return (
    <>
      <dt className="pt-0.5 text-[11px] font-bold uppercase tracking-wider text-charcoal/60">
        {label}
      </dt>
      <dd className="text-charcoal">
        {readOnly || !onChange ? (
          value || placeholder || ''
        ) : (
          <EditableText value={value} onChange={onChange} placeholder={placeholder} />
        )}
      </dd>
    </>
  );
}

/**
 * MetaDate — renders a native <input type="date"> styled inline so it looks
 * like a regular metadata row. When readOnly (finalized meeting) or there's
 * no onChange handler (paginator measure pass), falls back to a human-
 * readable date rendering.
 */
function MetaDate({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  readOnly: boolean;
}) {
  return (
    <>
      <dt className="pt-0.5 text-[11px] font-bold uppercase tracking-wider text-charcoal/60">
        {label}
      </dt>
      <dd className="text-charcoal">
        {readOnly || !onChange ? (
          formatHumanDate(value) || '-'
        ) : (
          <input
            {...NO_AUTOFILL}
            type="date"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="-mx-0.5 rounded-sm border-0 bg-transparent px-0.5 py-0 text-[13px] text-charcoal hover:bg-pine/5 focus:bg-pine/5 focus:outline-none"
            aria-label={`${label} (ISO format)`}
            // Suppress the autofill overlays from common password managers.
            // Dates are not credentials; those icons are noise here.
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            data-form-type="other"
          />
        )}
      </dd>
    </>
  );
}

/** Render an ISO YYYY-MM-DD as "Month D, YYYY" without timezone drift. */
function formatHumanDate(iso: string | undefined | null): string {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts.map((p) => Number.parseInt(p, 10));
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function Section({
  title,
  children,
  addSlot,
}: {
  title: string;
  children?: ReactNode;
  addSlot?: ReactNode;
}) {
  // Less-restrictive Word-doc feel — softer section labels, no hard
  // border below. The label sits as a subtle pine eyebrow rather
  // than a visual wall between blocks. addSlot button surfaces only
  // on hover so the body content is the primary thing the eye lands
  // on, like a real document.
  return (
    <section className="group/section">
      <div className="mb-1.5 flex items-baseline justify-between">
        <h2 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pine/65">
          {title}
        </h2>
        {addSlot && (
          <div className="text-xs opacity-0 transition-opacity group-hover/section:opacity-100">
            {addSlot}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function EmptyHint({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div className="text-[13px] italic text-smoke">
      {text}
      {action && <>{' '}{action}</>}
    </div>
  );
}

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 self-start text-[11px] text-smoke/70 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100"
      aria-label={label}
      title={label}
    >
      ✕
    </button>
  );
}

function SectionAddMenu({ items }: { items: Array<{ label: string; onClick: () => void }> }) {
  return (
    <div className="flex gap-3 opacity-0 transition-opacity group-hover/section:opacity-100">
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          onClick={it.onClick}
          className="text-[11px] text-smoke hover:text-pine"
        >
          + {it.label}
        </button>
      ))}
    </div>
  );
}
